import { fenToGameState, gameStateToFen } from '@/lib/fen-parser';
import { type GameState, type Position, validateMove } from '@/lib/chess-engine';

/**
 * Converts chess notation between SAN (Standard Algebraic Notation) and UCI (Universal Chess Interface) formats
 */

export interface MoveConversion {
  uci: string;
  san: string;
  from: Position;
  to: Position;
}

/**
 * Checks if a move string is in UCI format (e.g., "e2e4", "a7a8q")
 */
export function isUciFormat(move: string): boolean {
  // UCI format: [a-h][1-8][a-h][1-8][qrbn]?
  return /^[a-h][1-8][a-h][1-8][qrbnQRBN]?$/.test(move);
}

/**
 * Checks if a move string is in SAN format (e.g., "Nf3", "O-O", "Qxf7#")
 */
export function isSanFormat(move: string): boolean {
  // SAN patterns: piece moves, castling, pawn moves with captures, checks, etc.
  return /^([NBRQK])?([a-h]?[1-8]?)?x?[a-h][1-8](=[QRBN])?[+#]?$|^O-O(-O)?[+#]?$/.test(move);
}

/**
 * Converts algebraic notation (like "e2") to position array [row, col]
 */
function algebraicToPosition(algebraic: string): Position {
  const col = algebraic.charCodeAt(0) - 97; // a=0, b=1, etc.
  const row = 8 - parseInt(algebraic[1]); // 8=0, 7=1, etc.
  return [row, col];
}

/**
 * Converts position array [row, col] to algebraic notation (like "e2")
 */
function positionToAlgebraic(pos: Position): string {
  const [row, col] = pos;
  return String.fromCharCode(97 + col) + (8 - row).toString();
}

/**
 * Attempts to convert a SAN move to UCI format by trying all possible moves
 */
export function sanToUci(gameState: GameState, sanMove: string): string | null {
  // Remove check/checkmate indicators and trim
  const cleanSan = sanMove.replace(/[+#]$/, '').trim();
  
  // Handle castling
  if (cleanSan === 'O-O' || cleanSan === '0-0') {
    const row = gameState.sideToMove === 'white' ? 7 : 0;
    return `${positionToAlgebraic([row, 4])}${positionToAlgebraic([row, 6])}`;
  }
  if (cleanSan === 'O-O-O' || cleanSan === '0-0-0') {
    const row = gameState.sideToMove === 'white' ? 7 : 0;
    return `${positionToAlgebraic([row, 4])}${positionToAlgebraic([row, 2])}`;
  }
  
  // Parse SAN components
  const match = cleanSan.match(/^([NBRQK])?([a-h]?[1-8]?)?x?([a-h][1-8])(=[QRBN])?$/);
  if (!match) return null;
  
  const [, piece = 'P', disambiguation = '', targetSquare, promotion] = match;
  const targetPos = algebraicToPosition(targetSquare);
  
  // Find all possible source positions for this piece type
  const possibleSources: Position[] = [];
  
  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 8; col++) {
      const pos: Position = [row, col];
      const boardPiece = gameState.board[row][col];
      
      if (!boardPiece) continue;
      if (boardPiece.color !== gameState.sideToMove) continue;
      
      // Check if piece type matches
      const pieceType = boardPiece.type.toUpperCase();
      if (piece !== 'P' && pieceType !== piece) continue;
      if (piece === 'P' && pieceType !== 'P') continue;
      
      // Check disambiguation
      if (disambiguation) {
        const sourceSquare = positionToAlgebraic(pos);
        if (!sourceSquare.includes(disambiguation)) continue;
      }
      
      // Check if this move is legal
      const moveResult = validateMove(gameState, pos, targetPos);
      if (moveResult.isLegal) {
        possibleSources.push(pos);
      }
    }
  }
  
  // Return the first valid move (or null if none found)
  if (possibleSources.length > 0) {
    const source = possibleSources[0];
    let uci = positionToAlgebraic(source) + positionToAlgebraic(targetPos);
    
    // Add promotion if specified
    if (promotion) {
      uci += promotion.slice(1).toLowerCase(); // Remove '=' and make lowercase
    }
    
    return uci;
  }
  
  return null;
}

/**
 * Converts UCI move to SAN format with improved accuracy
 */
export function uciToSan(gameState: GameState, uciMove: string): string {
  if (uciMove.length < 4) return uciMove;
  
  const from = algebraicToPosition(uciMove.slice(0, 2));
  const to = algebraicToPosition(uciMove.slice(2, 4));
  const promotion = uciMove.length > 4 ? uciMove[4].toUpperCase() : null;
  
  const piece = gameState.board[from[0]][from[1]];
  if (!piece) return uciMove;
  
  // Handle castling
  if (piece.type.toLowerCase() === 'k' && Math.abs(from[1] - to[1]) === 2) {
    return to[1] > from[1] ? 'O-O' : 'O-O-O';
  }
  
  // Check if target square is occupied (capture)
  const isCapture = gameState.board[to[0]][to[1]] !== null;
  
  // Build SAN notation
  let san = '';
  
  // Piece letter (except for pawns)
  if (piece.type.toLowerCase() !== 'p') {
    san += piece.type.toUpperCase();
  } else if (isCapture) {
    // For pawn captures, include the file letter
    san += String.fromCharCode(97 + from[1]);
  }
  
  // Add capture indicator
  if (isCapture) {
    san += 'x';
  }
  
  // Target square
  san += positionToAlgebraic(to);
  
  // Promotion
  if (promotion) {
    san += '=' + promotion;
  }
  
  return san;
}

/**
 * Normalizes a move array to UCI format
 */
export function normalizeMoveArrayToUci(gameState: GameState, moves: string[]): string[] {
  const result: string[] = [];
  let currentGameState = { ...gameState };
  
  for (const move of moves) {
    if (!move || move.trim() === '') continue;
    
    const cleanMove = move.trim();
    let uciMove: string | null = null;
    
    if (isUciFormat(cleanMove)) {
      uciMove = cleanMove;
    } else if (isSanFormat(cleanMove)) {
      uciMove = sanToUci(currentGameState, cleanMove);
    }
    
    if (!uciMove) {
      console.warn(`Could not convert move: ${cleanMove}, skipping and continuing with next moves`);
      continue; // Skip this move but continue processing others
    }
    
    result.push(uciMove);
    
    // Apply the move to update the game state for the next move in sequence
    try {
      const parsed = parseUciMove(uciMove);
      if (parsed) {
        const moveResult = validateMove(currentGameState, parsed.from, parsed.to);
        if (moveResult.isLegal && moveResult.newState) {
          currentGameState = moveResult.newState;
        }
      }
    } catch (error) {
      console.warn(`Failed to apply move ${uciMove} for sequence processing:`, error);
      // Continue with the original game state - better to process what we can
    }
  }
  
  return result;
}

/**
 * Parse UCI move helper - moved from engine-adapter
 */
function parseUciMove(uciMove: string): { from: Position; to: Position; promotion?: string } | null {
  if (uciMove.length < 4) return null;
  
  const from = algebraicToPosition(uciMove.slice(0, 2));
  const to = algebraicToPosition(uciMove.slice(2, 4));
  const promotion = uciMove.length > 4 ? uciMove[4] : undefined;
  
  return { from, to, promotion };
}

/**
 * Sanitizes and normalizes a solution array
 */
export function sanitizeSolution(fen: string, solution: string[]): string[] {
  if (!solution || solution.length === 0) return [];
  
  const gameState = fenToGameState(fen);
  if (!gameState) return solution;
  
  return normalizeMoveArrayToUci(gameState, solution);
}